import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SUBSCRIPTION_BLOCKED_MESSAGE } from '../../utils/premiumAccess';

const FONT = "'DM Sans','Segoe UI',sans-serif";
const THEME = 'var(--activemenu, #3d91e4)';

const FEATURE_COPY = {
  'User Management': {
    blurb: 'Invite teammates, assign roles, and control who can access this workspace.',
    points: ['Invite and manage users', 'Role-based access', 'Team collaboration'],
  },
  'LLM Gateway': {
    blurb: 'Route, secure, and observe model traffic through a managed enterprise gateway.',
    points: ['Multi-provider routing', 'Key & spend controls', 'Traffic observability'],
  },
  Cloud: {
    blurb: 'Connect cloud accounts and provision infrastructure from one workspace.',
    points: ['Multi-cloud connectors', 'Secure credentials', 'Environment setup'],
  },
  Cluster: {
    blurb: 'Deploy and operate Kubernetes clusters with guided setup and monitoring.',
    points: ['Cluster provisioning', 'Health visibility', 'Workload readiness'],
  },
  Storage: {
    blurb: 'Store and manage project files with secure, scalable object storage.',
    points: ['Secure file storage', 'Project organization', 'Team access controls'],
  },
  Xray: {
    blurb: 'Import existing codebases and review architecture, health, and findings in one place.',
    points: ['GitHub and archive import', 'Architecture insights', 'Finding-driven fixes'],
  },
  'Audit history': {
    blurb: 'Review every governance decision, the policy that matched, and the context that was evaluated.',
    points: ['Full decision timeline', 'Policy and plugin trace', 'Filter by job, type, and outcome'],
  },
  App: {
    blurb: 'Build and ship production-ready apps from a prompt, with preview and deploy in one workspace.',
    points: ['Prompt-to-app generation', 'Live preview', 'Ship to production'],
  },
  'App + Agents': {
    blurb: 'Create apps with autonomous agents that can plan, act, and complete workflows for you.',
    points: ['App generation', 'Autonomous agents', 'End-to-end workflows'],
  },
  Website: {
    blurb: 'Generate a complete website from a prompt and iterate until it is ready to publish.',
    points: ['Prompt-to-website', 'Live preview', 'Publish-ready output'],
  },
};

const DEFAULT_COPY = {
  blurb: 'Unlock advanced SuperAgentX capabilities with a paid plan.',
  points: ['Priority support', 'Advanced integrations', 'Higher usage limits'],
};

function LockMark() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5" y="10" width="14" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M8 10V7.5a4 4 0 0 1 8 0V10"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <circle cx="12" cy="15.5" r="1.35" fill="currentColor" />
    </svg>
  );
}

function CheckMark() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2.5 6.2l2.3 2.3 4.7-4.8"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3 7h8M7.5 3.5L11 7l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Premium upgrade panel for POC-gated settings features.
 */
export default function PremiumFeatureLocked({
  featureName = 'This feature',
  message = SUBSCRIPTION_BLOCKED_MESSAGE,
  style,
  compact = false,
  onClose,
  onUpgrade,
}) {
  const navigate = useNavigate();
  const copy = FEATURE_COPY[featureName] || DEFAULT_COPY;

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
      return;
    }
    navigate('/settings?view=credit-usage');
  };

  const handleSecondary = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate('/apps');
  };

  return (
    <div
      style={{
        fontFamily: FONT,
        minHeight: compact ? 'auto' : '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: compact ? 0 : '40px 20px',
        position: 'relative',
        overflow: compact ? 'visible' : 'hidden',
        background: compact ? 'transparent' : '#f4f7fb',
        ...style,
      }}
    >
      <style>{`
        @keyframes pfRise {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pfPulse {
          0%, 100% { opacity: 0.45; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.04); }
        }
        .pf-shell { animation: pfRise 0.34s cubic-bezier(0.22,1,0.36,1); }
        .pf-orb {
          animation: pfPulse 5.5s ease-in-out infinite;
        }
        .pf-cta {
          transition: transform 0.16s ease, box-shadow 0.16s ease, filter 0.16s ease;
        }
        .pf-cta:hover {
          transform: translateY(-1px);
          filter: brightness(1.04);
          box-shadow: 0 6px 14px color-mix(in srgb, ${THEME} 28%, transparent) !important;
        }
        .pf-ghost:hover {
          background: #f8fafc !important;
          border-color: #cbd5e1 !important;
        }
        .pf-point {
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .pf-point:hover {
          background: #ffffff !important;
          border-color: color-mix(in srgb, ${THEME} 28%, #e2e8f0) !important;
        }
      `}</style>

      {!compact ? (
        <>
      {/* Atmosphere */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(15,23,42,0.045) 1px, transparent 0)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, #000 20%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, #000 20%, transparent 75%)',
          pointerEvents: 'none',
        }}
      />
      <div
        className="pf-orb"
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 420,
          height: 420,
          borderRadius: '50%',
          top: '8%',
          left: '50%',
          transform: 'translateX(-50%)',
          background: `radial-gradient(circle, color-mix(in srgb, ${THEME} 18%, transparent) 0%, transparent 68%)`,
          pointerEvents: 'none',
          filter: 'blur(8px)',
        }}
      />
        </>
      ) : null}

      <div
        className="pf-shell"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 560,
          zIndex: 1,
        }}
      >
        <div
          style={{
            background: compact ? '#ffffff' : 'rgba(255,255,255,0.92)',
            backdropFilter: compact ? 'none' : 'blur(14px)',
            WebkitBackdropFilter: compact ? 'none' : 'blur(14px)',
            border: '1px solid rgba(15,23,42,0.08)',
            borderRadius: 24,
            padding: '36px 32px 28px',
            boxShadow: compact
              ? '0 24px 60px rgba(15, 23, 42, 0.18), 0 1px 0 rgba(255,255,255,0.8) inset'
              : '0 1px 0 rgba(255,255,255,0.8) inset, 0 6px 18px rgba(15,23,42,0.04)',
          }}
        >
          {/* Icon + badge */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 14,
              marginBottom: 22,
            }}
          >
            <div
              style={{
                position: 'relative',
                width: 72,
                height: 72,
                borderRadius: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: THEME,
                background: `linear-gradient(160deg, color-mix(in srgb, ${THEME} 16%, #fff) 0%, #fff 100%)`,
                border: `1px solid color-mix(in srgb, ${THEME} 22%, #e2e8f0)`,
                boxShadow: `0 6px 14px color-mix(in srgb, ${THEME} 10%, transparent)`,
              }}
            >
              <LockMark />
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: '#0f172a',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  boxShadow: '0 2px 6px rgba(15,23,42,0.18)',
                }}
              >
                ✦
              </span>
            </div>

            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 28,
                padding: '0 12px',
                borderRadius: 999,
                background: '#0f172a',
                color: '#fff',
                fontSize: 11,
                fontWeight: 650,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Pro feature
            </span>
          </div>

          {/* Copy */}
          <div style={{ textAlign: 'center', maxWidth: 440, margin: '0 auto 22px' }}>
            <h2
              style={{
                margin: '0 0 10px',
                fontSize: 26,
                fontWeight: 750,
                color: '#0f172a',
                letterSpacing: '-0.04em',
                lineHeight: 1.2,
              }}
            >
              Unlock {featureName}
            </h2>
            <p
              style={{
                margin: '0 0 8px',
                fontSize: 14.5,
                lineHeight: 1.55,
                color: '#475569',
              }}
            >
              {copy.blurb}
            </p>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                lineHeight: 1.5,
                color: '#94a3b8',
              }}
            >
              {message}
            </p>
          </div>

          {/* Benefits */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 10,
              marginBottom: 26,
            }}
          >
            {copy.points.map((point) => (
              <div
                key={point}
                className="pf-point"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '12px 12px',
                  borderRadius: 14,
                  background: '#f8fafc',
                  border: '1px solid rgba(15,23,42,0.06)',
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 7,
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `color-mix(in srgb, ${THEME} 14%, #ffffff)`,
                    color: THEME,
                    marginTop: 1,
                  }}
                >
                  <CheckMark />
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 550,
                    color: '#334155',
                    lineHeight: 1.35,
                  }}
                >
                  {point}
                </span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10,
              justifyContent: 'center',
            }}
          >
            <button
              type="button"
              className="pf-cta"
              onClick={handleUpgrade}
              style={{
                height: 44,
                padding: '0 22px',
                borderRadius: 12,
                border: 'none',
                background: `linear-gradient(135deg, ${THEME} 0%, color-mix(in srgb, ${THEME} 55%, #0f172a) 100%)`,
                color: '#fff',
                fontFamily: FONT,
                fontSize: 14,
                fontWeight: 650,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: `0 4px 12px color-mix(in srgb, ${THEME} 22%, transparent)`,
              }}
            >
              Upgrade plan
              <ArrowIcon />
            </button>
            <button
              type="button"
              className="pf-ghost"
              onClick={handleSecondary}
              style={{
                height: 44,
                padding: '0 18px',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                background: '#fff',
                color: '#0f172a',
                fontFamily: FONT,
                fontSize: 14,
                fontWeight: 550,
                cursor: 'pointer',
                transition: 'background 0.15s ease, border-color 0.15s ease',
              }}
            >
              Back to home
            </button>
          </div>
        </div>

        <p
          style={{
            margin: '14px 0 0',
            textAlign: 'center',
            fontSize: 12,
            color: '#94a3b8',
          }}
        >
          Available on paid SuperAgentX plans
        </p>
      </div>
    </div>
  );
}
